/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Container from '@ba-ui-toolkit/container';
import { classnames as cx, propsUtils } from '@ba-ui-toolkit/utils';
import './FlexLayout.scss';

const { sanitize } = propsUtils;

export default class FlexLayout extends Component {
	static __FlexLayout__ = true;

	static propTypes = {
		className: PropTypes.string,
		outerClassName: PropTypes.string,
		children: PropTypes.node,
		fullHeight: PropTypes.bool,
		style: PropTypes.object,
		direction: PropTypes.oneOf(['column', 'row', 'row-reverse', 'column-reverse']),
		alignItems: PropTypes.oneOf(['stretch', 'center', 'flex-start', 'flex-end', 'baseline']),
		wrap: PropTypes.oneOf(['nowrap', 'wrap', 'wrap-reverse']),
		justifyContent: PropTypes.oneOf([
			'flex-start',
			'flex-end',
			'center',
			'space-between',
			'space-around',
		]),
		inline: PropTypes.bool,
		width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		outer: PropTypes.element,
	};

	static defaultProps = {
		className: '',
		maxWidth: '100%',
	};

	render() {
		const {
			className,
			style,
			fullHeight,
			direction,
			alignItems,
			children,
			wrap,
			justifyContent,
			inline,
			width,
			height,
			minHeight,
			minWidth,
			maxHeight,
			maxWidth,
			outer,
			outerClassName,
			...others
		} = this.props;

		const classNames = cx('ba-common-flexLayout', { fullHeight });

		const styles = {
			...style,
			flexDirection: direction,
			flexWrap: wrap,
			alignItems,
			justifyContent,
			width,
			height,
			minHeight,
			minWidth,
			maxHeight,
			maxWidth,
		};

		const inner = fullHeight
			? React.Children.map(children, child =>
					child && child.type && child.type.__FlexItem__ // eslint-disable-line no-underscore-dangle
						? React.cloneElement(child, {
								rowLayout: direction === 'row' || direction === 'row-reverse',
								fullHeightLayout: true,
						  })
						: child
			  )
			: children;

		const display = inline ? 'inline-flex' : 'flex';
		if (
			(direction == null || direction === 'row' || direction === 'row-reverse') &&
			alignItems != null
		) {
			// The following is specifically to fix IE11 layout issues with align items inside a row flex layout.
			// IE 11 does not correctly align items unless the flex layout has a fixed height.
			// To get around this we create a column layout and align the items vertically inside it.
			const outerStyle = {
				display,
				maxWidth: '100%',
				flexDirection: 'column',
				justifyContent: alignItems,
			};
			if (inline) {
				outerStyle.verticalAlign = 'middle';
			}

			return (
				<div style={outerStyle} className={outerClassName}>
					{outer}
					<div
						{...sanitize(others, FlexLayout)}
						className={`${classNames} ${className}`}
						style={{ ...styles }}
					>
						{inner}
					</div>
				</div>
			);
		}

		styles.display = display;
		if (inline) {
			styles.verticalAlign = 'middle';
		}

		return (
			<div
				{...sanitize(others, FlexLayout)}
				className={`${classNames} ${className}`}
				style={styles}
			>
				{inner}
			</div>
		);
	}
}

export class FlexItem extends Component {
	static __FlexItem__ = true;

	static propTypes = {
		className: PropTypes.string,
		children: PropTypes.node,
		grow: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
		shrink: PropTypes.bool,
		relative: PropTypes.bool,
		textAlign: PropTypes.oneOf(['left', 'right', 'center']),
		basis: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		overflow: PropTypes.string,
		style: PropTypes.object,
		fullHeightLayout: PropTypes.bool,
		rowLayout: PropTypes.bool,
		order: PropTypes.number,
		alignSelf: PropTypes.oneOf(['stretch', 'center', 'flex-start', 'flex-end', 'baseline']),

		// a means to opt out of the flex layout bug fix
		simpleContent: PropTypes.bool,
	};

	static defaultProps = {
		className: '',
	};

	render() {
		const {
			className,
			style,
			order,
			grow,
			shrink,
			relative,
			alignSelf,
			textAlign,
			basis,
			overflow,
			fullHeightLayout,
			rowLayout,
			simpleContent,
			...others
		} = this.props;
		let { children } = this.props;
		const classNames = cx('ba-common-flexLayout-item');

		let flexGrow;
		if (grow === true) {
			flexGrow = 1;
		} else {
			flexGrow = grow > 0 ? grow : 0;
		}

		let flexShrink;
		if (shrink != null) {
			flexShrink = shrink ? 1 : 0;
		} else {
			flexShrink = flexGrow;
		}

		const flexBasis = basis || (grow ? 0 : null);
		const styles = {
			...style,
			flexBasis,
			flexGrow,
			flexShrink,
			alignSelf,
			order,
		};
		if (relative) {
			styles.position = 'relative';
		}

		// Work around ancient Safari bug https://bugs.webkit.org/show_bug.cgi?id=137730
		// If a nested child is a flex layout with fullHeight then we need to wrap it.
		// We also need to do this to allow a child components to use a height of 100% without causing
		// the flex layout to grow beyond its bounds.
		// And if a flex layout has direction row and any child flex item nests a flex layout with full height.
		// In this case the flex item should also have a width that's not dependent on the child content. There's several ways
		// to achieve this so we cannot assume to be able to check this case in code here.
		if (children != null && React.Children.count(children) === 1) {
			const child = React.Children.toArray(children).filter(c => c)[0];
			if (
				child &&
				((!simpleContent && grow && fullHeightLayout) ||
					(rowLayout &&
						child.type &&
						child.type.__FlexLayout__ &&
						child.props.fullHeight))
			) {
				// eslint-disable-line no-underscore-dangle
				styles.position = 'relative';
				children = (
					<div className="ba-common-flexLayout__sw" style={{ overflow }}>
						{children}
					</div>
				);
			} else {
				styles.overflow = overflow;
			}
		} else {
			styles.overflow = overflow;
		}

		return (
			<Container
				{...sanitize(others, FlexItem)}
				className={`${classNames} ${className}`}
				style={styles}
				textAlign={textAlign}
			>
				{children}
			</Container>
		);
	}
}
